import { useSelectedPrimaryImage } from '~/hooks/image/useSelectedImages';
import { useRef } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilCallback, useRecoilState, useRecoilValue } from 'recoil';
import { useActiveFusionTarget } from '~/hooks/target/useActiveFusionTarget';
import { getRegistrationUndoService } from '~/domain/ServiceContainer';
import {
    RegistrationFailure,
    registerRigid,
} from '~/lib/registration-engine/registerRigid';
import {
    activeVoiConsumerState,
    autoRegistrationInProgressState,
    voiBoundsState,
} from '~/state/atoms/toolsAtoms';
import { handleError } from '~/utils/errors';
import { enqueueSnackbar } from '~/utils/notifications';

/**
 * Runs an automatic rigid registration of the primary row's fused pair,
 * scoped to the VOI box while it is shown, and writes the fitted matrix into
 * the pair's registration through its Environment, with an undo point
 * before it. The registration engine fits it from the registration's
 * current alignment. The registration must be the one a drag from the primary
 * edits, on a pair whose Environment is resident.
 *
 * A run is cancelled by `cancelProcessing`, or by anything that sets
 * `autoRegistrationInProgressState` false while it runs. A result whose
 * registration is no longer the one the pair's Environment holds, or was
 * approved meanwhile, is discarded and reported: it was fitted for
 * another pair.
 */
export function useAutoRegistration() {
    const intl = useIntl();
    const primaryImage = useSelectedPrimaryImage();
    const {
        secondaryImage,
        registration: selectedRegistration,
        environment,
    } = useActiveFusionTarget();
    // The fit is restricted to the box only while the box is on screen.
    const voiBounds = useRecoilValue(voiBoundsState);
    const isVoiShown =
        useRecoilValue(activeVoiConsumerState) === 'registration';
    const registrationUndoService = getRegistrationUndoService();

    const [isSubmitting, setIsSubmitting] = useRecoilState(
        autoRegistrationInProgressState
    );
    // A run is alive while it is the latest one started here and the
    // progress atom still says so.
    const runIdRef = useRef(0);
    const isRunAlive = useRecoilCallback(
        ({ snapshot }) =>
            (runId: number) =>
                runId === runIdRef.current &&
                snapshot
                    .getLoadable(autoRegistrationInProgressState)
                    .valueOrThrow(),
        []
    );

    const abortRef = useRef<AbortController | null>(null);

    const startProcessing = async () => {
        if (isSubmitting) {
            return;
        }

        if (
            !primaryImage ||
            !secondaryImage ||
            !selectedRegistration ||
            !environment
        ) {
            handleError('Missing required data for registration.');
            return;
        }

        runIdRef.current += 1;
        const runId = runIdRef.current;
        const abort = new AbortController();
        abortRef.current = abort;
        setIsSubmitting(true);
        try {
            const fitted = await registerRigid(
                primaryImage,
                secondaryImage,
                selectedRegistration.transformation,
                isVoiShown ? voiBounds : null,
                false,
                abort.signal
            );
            if (!isRunAlive(runId)) {
                return;
            }
            if (
                environment.registration !== selectedRegistration ||
                selectedRegistration.isApproved
            ) {
                enqueueSnackbar(
                    intl.formatMessage({ id: 'registration.auto.discarded' }),
                    { variant: 'warning' }
                );
                return;
            }
            registrationUndoService.pushState(
                selectedRegistration.dicomUid,
                selectedRegistration.transformation
            );
            environment.setRegistrationMatrix(fitted.matrix);
            selectedRegistration.syncTransformationToDataset();
            enqueueSnackbar(
                intl.formatMessage({ id: 'registration.auto.completed' }),
                { variant: 'success' }
            );
        } catch (error) {
            if (isRunAlive(runId)) {
                handleError(
                    error,
                    error instanceof RegistrationFailure
                        ? intl.formatMessage(
                              { id: 'registration.auto.failedBecause' },
                              { reason: error.reason }
                          )
                        : intl.formatMessage({ id: 'registration.auto.failed' })
                );
            }
        } finally {
            if (runId === runIdRef.current) {
                setIsSubmitting(false);
            }
        }
    };

    const cancelProcessing = () => {
        runIdRef.current += 1;
        abortRef.current?.abort();
        setIsSubmitting(false);
    };

    const hasSelectedRegistration = !!selectedRegistration;

    return {
        startProcessing,
        cancelProcessing,
        isSubmitting,
        hasSelectedRegistration,
    };
}
